/**
 * Host Context Merge Utilities
 *
 * Canonical merge logic for CharHostContext deltas. Used by both the host-side
 * web component and the iframe-side useHostContext hook to ensure identical
 * merge behavior on both sides of the postMessage bridge.
 */

import type { CharHostContext } from '../types'

export function mergeStyles(
	current: CharHostContext['styles'] | undefined,
	patch: CharHostContext['styles'] | undefined,
): CharHostContext['styles'] | undefined {
	if (!patch) return current
	return {
		...current,
		...patch,
		variables: patch.variables ?? current?.variables,
		fonts: patch.fonts ?? current?.fonts,
	}
}

export function shallowMerge<T extends object>(
	current: T | undefined,
	patch: T | undefined,
): T | undefined {
	if (!patch) return current
	return { ...current, ...patch }
}

export function mergeHostContext(
	current: CharHostContext | null,
	patch: CharHostContext,
): CharHostContext {
	const previous = current ?? {}
	return {
		...previous,
		...patch,
		styles: mergeStyles(previous.styles, patch.styles),
		containerDimensions: shallowMerge(previous.containerDimensions, patch.containerDimensions),
		deviceCapabilities: shallowMerge(previous.deviceCapabilities, patch.deviceCapabilities),
		safeAreaInsets: shallowMerge(previous.safeAreaInsets, patch.safeAreaInsets),
	}
}
